<template>
  <div class="recurrence-form modal-card">
    <header class="modal-card-head">
      <p class="modal-card-title">
        Custom recurrence
      </p>
      <button type="button" class="delete" @click="$emit('close')" />
    </header>
    <section class="modal-card-body has-text-left">
      <div class="is-flex is-justify-content-start">
        <b-field label="Repeat every" horizontal>
          <b-numberinput
            size="is-small"
            controls-position="compact"
            :min="1"
            :max="maxDuration"
            v-model="settings.repeat.every"
          ></b-numberinput>
        </b-field>
        <b-field class="ml-4">
          <b-select v-model="settings.repeat.basis" size="is-small">
            <option value="minute" v-if="allowMinute">minute</option>
            <option value="day">day</option>
            <option value="week">week</option>
            <option value="month">month</option>
          </b-select>
        </b-field>
      </div>
      <div
        class="is-flex is-justify-content-start mt-5"
        v-if="settings.repeat.basis === 'week'"
      >
        <b-field label="Repeat on" horizontal>
          <b-checkbox v-model="settings.repeat.on.weekdays" native-value="0">
            S
          </b-checkbox>
          <b-checkbox v-model="settings.repeat.on.weekdays" native-value="1">
            M
          </b-checkbox>
          <b-checkbox v-model="settings.repeat.on.weekdays" native-value="2">
            T
          </b-checkbox>
          <b-checkbox v-model="settings.repeat.on.weekdays" native-value="3">
            W
          </b-checkbox>
          <b-checkbox v-model="settings.repeat.on.weekdays" native-value="4">
            T
          </b-checkbox>
          <b-checkbox v-model="settings.repeat.on.weekdays" native-value="5">
            F
          </b-checkbox>
          <b-checkbox v-model="settings.repeat.on.weekdays" native-value="6">
            S
          </b-checkbox>
        </b-field>
      </div>
      <div
        class="is-flex is-justify-content-start mt-5"
        v-if="settings.repeat.basis === 'month'"
      >
        <b-field label="Repeat on" horizontal>
          <b-radio
            v-model="settings.repeat.on.monthType"
            native-value="absolute"
          >
            Day
          </b-radio>
          <b-radio
            v-model="settings.repeat.on.monthType"
            native-value="relative"
          >
            Weekday
          </b-radio>
        </b-field>
      </div>
      <div
        class="is-flex is-justify-content-start mt-1"
        v-if="settings.repeat.basis === 'month'"
        style="min-height: 42px;"
      >
        <div
          class="is-flex is-justify-content-start"
          style="padding-left: 142px;"
          v-if="settings.repeat.on.monthType === 'absolute'"
        >
          <span class="pr-2 pt-2 is-size-7">Day</span>
          <b-field>
            <b-numberinput
              size="is-small"
              controls-position="compact"
              :min="1"
              :max="31"
              v-model="settings.repeat.on.monthAbsolute"
            ></b-numberinput>
          </b-field>
          <span class="pl-2 pt-2 is-size-7">of the month</span>
        </div>
        <div
          class="is-flex is-justify-content-start"
          style="padding-left: 142px;"
          v-if="settings.repeat.on.monthType === 'relative'"
        >
          <span class="pr-2 pt-2 is-size-7">The</span>
          <b-select
            v-model="settings.repeat.on.monthRelative.which"
            size="is-small"
          >
            <option value="0">1st</option>
            <option value="1">2nd</option>
            <option value="2">3rd</option>
            <option value="3">4th</option>
          </b-select>
          <b-select
            v-model="settings.repeat.on.monthRelative.weekday"
            size="is-small"
          >
            <option value="1">Monday</option>
            <option value="2">Tuesday</option>
            <option value="3">Wednesday</option>
            <option value="4">Thursday</option>
            <option value="5">Friday</option>
            <option value="6">Saturday</option>
            <option value="0">Sunday</option>
          </b-select>
          <span class="pl-2 pt-2 is-size-7">of the month</span>
        </div>
      </div>
      <div class="is-flex is-justify-content-start mt-5">
        <b-field label="Ends" horizontal>
          <b-radio v-model="settings.ends.type" native-value="never">
            Never
          </b-radio>
          <b-radio v-model="settings.ends.type" native-value="date">
            On
          </b-radio>
          <b-radio v-model="settings.ends.type" native-value="occurrences">
            After
          </b-radio>
        </b-field>
      </div>
      <div
        class="is-flex is-justify-content-start mt-1"
        v-if="settings.ends.type === 'date'"
        style="padding-left: 142px; min-height: 42px;"
      >
        <b-field label="">
          <b-datepicker
            v-model="settings.ends.onDate"
            icon="calendar"
            size="is-small"
            trap-focus
            :date-formatter="formatter"
          ></b-datepicker>
        </b-field>
      </div>
      <div
        class="is-flex is-justify-content-start mt-1"
        v-if="settings.ends.type === 'occurrences'"
        style="padding-left: 142px; min-height: 42px;"
      >
        <span class="pr-2 pt-2 is-size-7">After</span>
        <b-field style="width: 120px;">
          <b-numberinput
            size="is-small"
            controls-position="compact"
            :min="1"
            v-model="settings.ends.maxOccurrences"
          ></b-numberinput>
        </b-field>
        <span class="pl-2 pt-2 is-size-7">occurrences</span>
      </div>
    </section>
    <footer class="modal-card-foot is-justify-content-flex-end">
      <b-button label="Clear" type="is-danger" @click="clearSchedule" />
      <b-button label="Set" type="is-primary" @click="setSchedule" />
    </footer>
  </div>
</template>

<script>
import _ from "lodash";
import { addDuration, formatDate } from "vue-chess-shared/utils/date-helper";

export default {
  name: "RecurrenceForm",
  props: ["initialSettings"],

  data() {
    const defaultSettings = {
      repeat: {
        every: 1,
        basis: "day",
        on: {
          monthType: "absolute",
          monthAbsolute: 1,
          monthRelative: {
            which: "0",
            weekday: "1", // monday
          },
          weekdays: ["0"], // 0 - 6: Sun - Mon
        },
      },
      ends: {
        type: "date",
        onDate: null,
        maxOccurrences: 30,
      },
    };
    return {
      settings: _.merge(defaultSettings, this.initialSettings),
      formatter: (date) => {
        return formatDate(date, "LL");
      },
    };
  },

  watch: {
    "settings.repeat.basis": {
      handler() {
        if (this.settings.repeat.every <= this.maxDuration) {
          return;
        }

        this.settings.repeat.every = 1;
      },
    },
  },

  computed: {
    allowMinute() {
      return process.env.VUE_APP_PROJECT_ID === "chesssquad-dev";
    },

    maxDuration() {
      return this.settings.repeat.basis === "month"
        ? 1
        : this.settings.repeat.basis === "week"
        ? 4
        : 31;
    },

    text() {
      const spans = [];

      if (this.settings.repeat.basis === "minute") {
        spans.push(
          `Every ${this.settings.repeat.every} minute${
            this.settings.repeat.every > 1 ? "s" : ""
          },`
        );
      } else {
        if (this.settings.repeat.every > 1) {
          spans.push(`Every ${this.settings.repeat.every}`);
          if (this.settings.repeat.basis === "day") {
            spans.push("days,");
          } else if (this.settings.repeat.basis === "week") {
            spans.push("weeks,");
          } else {
            spans.push("months,");
          }
        } else {
          if (this.settings.repeat.basis === "day") {
            spans.push("Daily,");
          } else if (this.settings.repeat.basis === "week") {
            spans.push("Weekly,");
          } else {
            spans.push("Monthly,");
          }
        }
      }

      if (this.settings.repeat.basis === "week") {
        const sortedWeekdays = [...this.settings.repeat.on.weekdays].sort();
        const weekdays = sortedWeekdays.join(",");
        if (weekdays === "1,2,3,4,5") {
          spans.push("on weekdays,");
        } else if (weekdays === "0,6") {
          spans.push("on weekend,");
        } else if (weekdays === "0,1,2,3,4,5,6") {
          spans.push("on every day of the week,");
        } else {
          const daysOfWeek = sortedWeekdays.map((wd) => {
            return this.formatWeekday(wd);
          });
          spans.push(`on ${daysOfWeek.join(",")},`);
        }
      } else if (this.settings.repeat.basis === "month") {
        if (this.settings.repeat.on.monthType === "absolute") {
          spans.push(
            `on day ${this.settings.repeat.on.monthAbsolute} of the month,`
          );
        } else {
          const relativeWeek = this.formatRelativeWhich(
            this.settings.repeat.on.monthRelative.which
          );
          const weekday = this.formatWeekday(
            this.settings.repeat.on.monthRelative.weekday
          );

          spans.push(`on the ${relativeWeek} ${weekday} of the month,`);
        }
      }

      if (this.settings.ends.type === "date") {
        spans.push(`ends on ${formatDate(this.settings.ends.onDate, "LL")}`);
      } else if (this.settings.ends.type === "occurrences") {
        spans.push(
          `ends after ${this.settings.ends.maxOccurrences} occurrences`
        );
      } else {
        spans.push(`never ends`);
      }

      return spans.join(" ");
    },
  },

  methods: {
    setSchedule() {
      if (
        this.settings.repeat.basis === "week" &&
        !this.settings.repeat.on.weekdays.length
      ) {
        this.$buefy.toast.open({
          message: "Repeat on cannot be empty",
          type: "is-danger",
        });
        return;
      }

      this.$emit("submit", {
        repeat: this.settings.repeat,
        ends: {
          ...this.settings.ends,
          onDate: this.settings.ends.onDate
            ? formatDate(this.settings.ends.onDate, "YYYY-MM-DD")
            : null,
        },
        text: this.text,
      });
    },

    clearSchedule() {
      this.$emit("submit");
    },

    formatWeekday(wd) {
      if (wd === "0") {
        return "Sun";
      } else if (wd === "1") {
        return "Mon";
      } else if (wd === "2") {
        return "Tue";
      } else if (wd === "3") {
        return "Wed";
      } else if (wd === "4") {
        return "Thu";
      } else if (wd === "5") {
        return "Fri";
      } else {
        return "Sat";
      }
    },

    formatRelativeWhich(which) {
      if (which === "0") {
        return "1st";
      } else if (which === "1") {
        return "2nd";
      } else if (which === "2") {
        return "3rd";
      } else {
        return "4th";
      }
    },
  },

  created() {
    if (this.settings.ends.onDate) {
      this.settings.ends.onDate = new Date(this.settings.ends.onDate);
    } else {
      this.settings.ends.onDate = addDuration(new Date(), 30, "day");
    }
  },
};
</script>

<style>
.recurrence-form .field.is-horizontal .field-label {
  min-width: 120px;
}
.recurrence-form .field .help.is-danger {
  width: 120px;
}
.recurrence-form.modal-card {
  overflow: visible;
}
.recurrence-form .modal-card-body {
  overflow: visible;
}
</style>
